import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Clock, Users, Heart, Shield, ArrowRight, Quote, Star } from 'lucide-react';
import CTA from '../components/CTA';
import ServiceCard from '../components/ServiceCard';
import FreeGuideCTA from '../components/FreeGuideCTA';

const Home: React.FC = () => {
  const services = [
    {
      title: "Initial Consultation",
      description: "Free 10-minute discovery call to answer questions and see if we're a good fit.",
      bullets: [
        "Quick 10-minute phone call",
        "Ask questions about hypnotherapy",
        "Understand how sessions work",
        "Clear next steps if it's a fit",
        "No obligation to continue"
      ],
      icon: <Heart className="w-6 h-6" />
    },
    {
      title: "Follow-On Hypnotherapy",
      description: "Regular hypnotherapy sessions to address your specific challenges and achieve lasting change.",
      bullets: ["Solution-focused approach", "Neuroplasticity techniques", "6-8 sessions recommended", "Flexible scheduling"],
      icon: <Users className="w-6 h-6" />
    },
    {
      title: "Fears, Phobias & PTSD",
      description: "Specialized 3-session program to overcome fears, phobias, and post-traumatic stress disorder.",
      bullets: ["Rapid resolution techniques", "Trauma-informed approach", "3-session intensive", "Long-lasting results"],
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: "Stop Smoking & Vaping",
      description: "Intensive 2-hour session to break free from smoking and vaping addiction permanently.",
      bullets: ["2-hour intensive session", "Immediate results", "Lifetime support", "Money-back guarantee"],
      icon: <Shield className="w-6 h-6" />
    }
  ];


  return (
    <main>
      <Helmet>
        <title>Hypnotherapist Belfast & Lisburn | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Expert clinical hypnotherapy in Belfast & Lisburn. Reduce anxiety, manage stress, overcome trauma & break unwanted habits. In-person & online sessions. Free discovery call. Book today!"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-primary-50 via-white to-accent-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-accent-500/5"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6 leading-tight">
                When Fear Becomes a Habit,
                <span className="text-accent-600 block">the Body Can Learn Safety Again</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-6 leading-relaxed">
                I help people whose nervous system has learned fear patterns that show up as anxiety, physical symptoms, avoidance, bladder urgency, medical procedure anxiety and fear of treatment.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Lasting change in as little as 6–8 sessions. Free 10-minute consultation to explore your goals
                with Sharon Clare's expert <a href="/about" className="text-accent-600 hover:text-accent-700 underline">CNHC registered hypnotherapy</a> in <a href="/contact" className="text-accent-600 hover:text-accent-700 underline">Belfast</a>, <a href="/lisburn-hypnotherapy" className="text-accent-600 hover:text-accent-700 underline">Lisburn</a> and online across Northern Ireland.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="/contact"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  Book Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <a
                  href="/services"
                  className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  Explore Services
                </a>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="flex items-center justify-center space-x-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold">Reduce Stress & Anxiety</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold">Improve Sleep</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold">Break Habits</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold">Boost Resilience</span>
                </div>
              </div>
            </div>
            
            <div className="relative mt-8 mb-8">
              <div className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-gray-200 relative z-10">
                <picture>
                  <source srcSet="/images/sharon-clare-profile.webp" type="image/webp" />
                  <img
                    src="/images/Sharon Clare New Profile Picture.jpeg"
                    loading="lazy"
                    alt="Sharon Clare - CNHC Registered Clinical Hypnotherapist in Belfast, Northern Ireland"
                    width="600"
                    height="899"
                    className="w-full h-auto rounded-2xl"
                  />
                </picture>
                <div className="mt-6 text-center">
                  <p className="text-2xl font-heading font-semibold text-gray-900 mb-2">
                    Sharon Clare
                  </p>
                  <p className="text-accent-600 font-medium mb-4">
                    Clinical Hypnotherapist • Medical Procedure Anxiety & Health Focus
                  </p>
                  <div className="flex justify-center space-x-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" aria-hidden="true" />Experienced Clinical Hypnotherapist</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" aria-hidden="true" />100+ Clients Helped</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Body Betraying You" video */}
      <section className="py-16 bg-white" aria-label="Body Betraying You video">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4 text-center">
            Many people think their body has suddenly started betraying them
          </h2>
          <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100 shadow-lg">
            <video
              controls
              preload="metadata"
              playsInline
              className="w-full h-full"
              src="/videos/body-betraying-you.mp4"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white mt-16">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
              How I Can Help You
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Specialized hypnotherapy treatments designed to address your specific needs 
              and help you achieve lasting transformation.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a
              href="/services"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center"
            >
              View All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Assessment Quiz CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-accent-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Not Sure Where to Start?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Take our free 2-minute assessment to discover which hypnotherapy approach
              is best suited for your unique needs. Get personalized recommendations instantly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/assessment"
                className="bg-white text-accent-600 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg inline-flex items-center shadow-lg"
              >
                Take Free Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="/contact"
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-colors font-semibold text-lg inline-flex items-center"
              >
                Book Free Consultation
              </a>
            </div>
            <div className="mt-8 flex items-center justify-center space-x-8 text-white/80 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Takes 2 minutes</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Personalized results</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>100% confidential</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic Location */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
              Two Convenient Clinics + Online Sessions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Access professional CNHC registered hypnotherapy at our Belfast clinic on the Ormeau Road, our Lisburn clinic at The Arzen Collective, or from the comfort of your home via online sessions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Belfast Location */}
            <a
              href="/belfast-hypnotherapy"
              className="group bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-accent-200"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-heading font-bold text-gray-900">Belfast Clinic</h3>
                <ArrowRight className="w-6 h-6 text-accent-600 group-hover:translate-x-1 transition-transform" />
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Belfast Wellness Clinic, 228 Ormeau Road, Belfast</p>
                    <p className="text-sm text-gray-600">Belfast BT7 2FZ</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">Clinic days and times vary, and early evening appointments are available.</p>
                    <a
                      href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-accent-600 hover:text-accent-700 underline"
                    >
                      Check current availability and book
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">South Belfast, about 10 minutes from the city centre</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 font-semibold">Perfect for:</p>
                <p className="text-sm text-gray-600">Radiotherapy patients, medical anxiety, Belfast & North Down residents</p>
              </div>

              <div className="mt-4 text-accent-600 font-semibold group-hover:text-accent-700 flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </a>

            {/* Lisburn Location */}
            <a
              href="/lisburn-hypnotherapy"
              className="group bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-accent-200"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-heading font-bold text-gray-900">Lisburn Clinic</h3>
                <ArrowRight className="w-6 h-6 text-accent-600 group-hover:translate-x-1 transition-transform" />
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">The Arzen Collective, 13B Antrim Street</p>
                    <p className="text-sm text-gray-600">Lisburn BT28</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">Appointment times vary</p>
                    <p className="text-sm text-gray-700">Early evening appointments available</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">Lisburn city centre</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 font-semibold">Perfect for:</p>
                <p className="text-sm text-gray-600">Lisburn, Craigavon, Antrim & south-west Belfast residents</p>
              </div>

              <div className="mt-4 text-accent-600 font-semibold group-hover:text-accent-700 flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </a>

            {/* Online Sessions */}
            <div
              className="group bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 border-2 border-transparent"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-heading font-bold text-gray-900">Online Sessions</h3>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Secure Video Platform</p>
                    <p className="text-sm text-gray-600">Sessions from the comfort of your home</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">Flexible scheduling</p>
                    <p className="text-xs text-gray-500">Daytime & evening available</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Star className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">Available across Northern Ireland & UK</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 font-semibold">Perfect for:</p>
                <p className="text-sm text-gray-600">Anyone across NI & UK, busy schedules, those who prefer home sessions</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              <strong>In-person</strong> in Belfast or Lisburn, or <strong>online</strong> from anywhere in Northern Ireland and the UK
            </p>
            <a
              href="/contact"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center"
            >
              Book Your Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Featured Testimonial - Mark's Story */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
              Client Success Story
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real transformation through specialized radiotherapy anxiety support.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-accent-500 to-primary-500 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
              <Quote className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <blockquote className="text-lg md:text-xl lg:text-2xl font-medium mb-8 leading-relaxed text-center">
                "Mark began radiotherapy for head and neck cancer last week. On his first day, wearing the mask was overwhelming—it reminded him of a frightening childhood experience, and his body went straight into panic mode. Even with medication, stress balls, and calming music, he couldn't complete treatment. After hypnotherapy support, Mark was able to feel calmer, cope better, and complete treatment that once felt impossible."
              </blockquote>
              <div className="flex items-center justify-center space-x-4 mb-8">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  M
                </div>
                <div className="text-left">
                  <div className="font-semibold text-lg">Mark's Story</div>
                  <div className="text-white/90">Head & Neck Cancer Patient</div>
                  <div className="flex items-center mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-center">
                <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium">
                  Radiotherapy Anxiety Support
                </span>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <a
              href="/testimonials"
              className="btn-secondary text-lg px-8 py-4 inline-flex items-center"
            >
              Read More Success Stories
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Free Guide Lead Magnet */}
      <FreeGuideCTA location="Home" />

      {/* CTA Section */}
      <CTA
        title="Ready to Start Your Transformation?"
        subtitle="Book your free 10-minute consultation today and take the first step towards a better life."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default Home;
